import useSWR from 'swr'
import axios from '@/lib/axios'
import { useEffect } from 'react'
import { useRouter } from 'next/router'

export function isEmailVerified(user) {
    return Boolean(user?.email_verified_at)
}

export function verifyEmailPath(email) {
    const base = '/verify-email'
    if (!email) return base
    return `${base}?email=${encodeURIComponent(email)}`
}

export function resetPasswordPath(email) {
    const base = '/reset-password'
    if (!email) return base
    return `${base}?email=${encodeURIComponent(email)}`
}

function readPendingResetEmail() {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('pending_reset_email')
}

function storePendingResetEmail(email) {
    if (typeof window === 'undefined' || !email) return
    localStorage.setItem('pending_reset_email', email)
}

function clearPendingResetEmail() {
    if (typeof window === 'undefined') return
    localStorage.removeItem('pending_reset_email')
}

export const useAuth = ({ middleware, redirectIfAuthenticated } = {}) => {
    const router = useRouter()

    const fetchCsrfToken = async () => {
        await axios.get('/sanctum/csrf-cookie')
    }

    const {
        data: user,
        error,
        mutate,
    } = useSWR('/api/user', () =>
        axios
            .get('/api/user')
            .then(res => res.data)
            .catch(err => {
                if (err.response?.status === 401) return null
                if (err.response?.status === 409) {
                    const email = err.response?.data?.email
                    router.push(verifyEmailPath(email))
                    return null
                }
                throw err
            }),
    )

    const loading = user === undefined && !error

    const register = async ({ setErrors, ...props }) => {
        await fetchCsrfToken()
        setErrors({})

        try {
            const { data, status } = await axios.post('/api/register', props)

            if (status === 201 || status === 200) {
                try {
                    await axios.post('/api/logout')
                } catch {
                    // not logged in yet
                }
                await mutate(null, false)

                const email = data?.user?.email || props?.email
                const target = data?.redirect_url || '/verify-email'
                router.push(
                    email
                        ? `${target}?email=${encodeURIComponent(email)}`
                        : target,
                )
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors)
            } else if (err.response?.status === 429) {
                setErrors({
                    email: ['Too many registration attempts. Please try again later.'],
                })
            } else {
                setErrors({ email: ['Registration failed. Please try again.'] })
            }
            throw err
        }
    }

    const login = async ({ setErrors, setStatus, ...props }) => {
        await fetchCsrfToken()
        setErrors({})
        setStatus(null)

        try {
            const { data } = await axios.post('/api/login', props)

            if (data?.user) {
                mutate(data.user, false)
            } else {
                await mutate()
            }

            if (data?.user && !isEmailVerified(data.user)) {
                router.push(verifyEmailPath(data.user.email || props.email))
                return data
            }

            router.push('/dashboard')
            return data
        } catch (err) {
            if (err.response?.status === 403 && err.response.data?.redirect_url) {
                router.push(err.response.data.redirect_url)
                return
            }
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors)
            } else if (err.response?.status === 429) {
                setErrors({
                    email: ['Too many login attempts. Please try again later.'],
                })
            } else {
                setErrors({ email: ['Login failed. Please try again.'] })
            }
            throw err
        }
    }

    const forgotPassword = async ({ setErrors, setStatus, setSuccess, email }) => {
        try {
            await fetchCsrfToken()
            setErrors([])
            setStatus(null)

            const response = await axios.post('/api/password/email', { email })

            if (response?.data?.status) {
                storePendingResetEmail(email)
                setStatus(response.data.status)
                if (typeof setSuccess === 'function') {
                    setSuccess('Password reset code sent to your email.')
                }
                return true
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors)
            } else {
                console.error('Forgot password error:', err)
            }
        }
        return false
    }

    const verifyPasswordResetCode = async ({ email, code, setErrors, setStatus }) => {
        await fetchCsrfToken()
        setErrors([])
        setStatus(null)

        const emailToUse = email || readPendingResetEmail()

        try {
            const response = await axios.post('/api/password/verify-code', {
                email: emailToUse,
                code: String(code).replace(/\D/g, '').slice(0, 6),
            })

            if (response.data?.status === 'code-verified') {
                setStatus('code-verified')
                return true
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors)
            } else {
                setErrors({ code: ['Invalid or expired reset code.'] })
            }
        }
        return false
    }

    const resetPassword = async ({ setErrors, setStatus, email, code, ...props }) => {
        await fetchCsrfToken()
        setErrors([])
        setStatus(null)

        const emailToUse = email || readPendingResetEmail()

        try {
            const response = await axios.post('/api/password/reset', {
                email: emailToUse,
                code: String(code).replace(/\D/g, '').slice(0, 6),
                password: props.password,
                password_confirmation: props.password_confirmation,
            })
            clearPendingResetEmail()
            router.push('/login?reset=' + btoa(response.data.status || 'password-reset'))
        } catch (err) {
            if (err.response?.status !== 422) throw err
            setErrors(err.response.data.errors)
        }
    }

    const resendEmailVerification = ({ setStatus }) => {
        axios
            .post('/api/email/verification-notification')
            .then(response => setStatus(response.data.status))
            .catch(err => {
                console.error('Resend verification error:', err)
            })
    }

    const logout = async () => {
        if (!error) {
            try {
                await axios.post('/api/logout')
            } catch (err) {
                console.error('Logout error:', err)
            }
        }

        mutate(null, false)
        router.push('/login')
    }

    useEffect(() => {
        if (loading || !router.isReady) return

        const onVerifyPage = router.pathname === '/verify-email'

        if (middleware === 'guest' && redirectIfAuthenticated && user) {
            if (isEmailVerified(user)) {
                router.push(redirectIfAuthenticated)
            } else if (!onVerifyPage) {
                router.push(verifyEmailPath(user.email))
            }
            return
        }

        if (onVerifyPage && user && isEmailVerified(user)) {
            router.push('/dashboard')
            return
        }

        if (middleware === 'auth') {
            if (user === null) {
                router.push('/login')
                return
            }
            if (user && !isEmailVerified(user)) {
                router.push(verifyEmailPath(user.email))
            }
        }
    }, [user, error, loading, router.isReady, router.pathname])

    return {
        user,
        register,
        login,
        forgotPassword,
        verifyPasswordResetCode,
        resetPassword,
        resendEmailVerification,
        logout,
        loading,
    }
}
